import {
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    OnInit,
    output,
    signal,
    untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { filter, map, tap } from 'rxjs';

import { FileSizePipe, StorageUploadProgressPayload, UploadService } from '@evc/platform';
import {
    ButtonComponent,
    Maybe,
    ProgressBarComponent,
    progressBarModes,
    progressBarStates,
    ValueOf,
} from '@evc/web-components';
import { TranslatePipe } from '@shared/services/i18n/i18n.module';

import * as UploadActions from '@app/store/actions/upload/upload.action';
import type { CurrentUploadState } from '@app/store/reducers';
import { RemainingTimePipe } from '@app/utils/pipes/remaining-time/remaining-time.pipe';

export const UploadFileStates = {
  UNKNOWN: 'unknown',
  PENDING: 'pending',
  PROGRESS: 'progress',
  COMPLETE: 'complete',
  CANCEL: 'cancel',
  ERROR: 'error',
} as const;
export type UploadFileState = ValueOf<typeof UploadFileStates>;

export type LocalStatus = {
  progress: Maybe<number>;
  uploaded: Maybe<number>;
  size: Maybe<number>;
  cancelled: boolean;
};
@Component({
  selector: 'drive-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrls: ['./upload-file.component.scss'],
  imports: [
    ButtonComponent,
    ProgressBarComponent,
    RemainingTimePipe,
    FileSizePipe,
    TranslatePipe,
  ],
})
export class UploadFileComponent implements OnInit {
  readonly cancelUpload = output<{ uploadId: string }>();
  current = input<CurrentUploadState>();

  #store = inject(Store);
  #uploadService = inject(UploadService);
  #destroyRef = inject(DestroyRef);
  protected progressBarModes = progressBarModes;
  protected progressBarStates = progressBarStates;
  protected UploadFileStates = UploadFileStates;

  state = computed<UploadFileState>(() => {
    const current = this.current();
    if (!current) return UploadFileStates.UNKNOWN;

    const { started, complete, failed, cancelled } = current;
    const { progress, cancelled: localCancelled } = this.localStatus();

    if (failed) return UploadFileStates.ERROR;
    if (complete || (progress ?? 0) >= 100) return UploadFileStates.COMPLETE;
    if (cancelled || localCancelled) return UploadFileStates.CANCEL;

    if (!started) return UploadFileStates.UNKNOWN;
    if (progress === 0) return UploadFileStates.PENDING;

    return UploadFileStates.PROGRESS;
  });

  cancellable = computed<boolean>(() => {
    const uploadState = this.current();
    if (!uploadState) return false;

    const { uploadId, failed, complete, cancelled } = uploadState;
    const isDone = failed || complete || cancelled || this.localStatus().cancelled;

    const haveValidId = uploadId && uploadId.startsWith('upload-');
    const haveValidFileName = !!uploadState.file?.name;
    const isValid = haveValidId || haveValidFileName;

    return isValid && !isDone;
  });

  localStatus = signal<LocalStatus>({
    progress: undefined,
    uploaded: undefined,
    size: undefined,
    cancelled: false,
  });

  #syncCurrentMetas = effect(() => {
    const current = this.current();
    if (!current) return;

    untracked(() => {
      const { progress, uploaded, size, cancelled } = current;
      this.localStatus.set({ progress, uploaded, size, cancelled });
    });
  });

  ngOnInit(): void {
    this.#destroyRef.onDestroy(() => {
      this.#syncCurrentMetas.destroy();
    });

    this.#uploadService.progressSubject
      .pipe(
        filter((progress) => !!progress),
        map<
          StorageUploadProgressPayload,
          [StorageUploadProgressPayload, Maybe<CurrentUploadState>]
        >((progress) => [
          progress,
          this.current(),
        ]),
        filter<[StorageUploadProgressPayload, CurrentUploadState]>(
          ([{ fileName }, uploadState]) => {
            const { file } = uploadState ?? {};

            return (file && file.name === fileName) as boolean;
          },
        ),
        tap({ next: this.#syncProgressSubject.bind(this) }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  cancelFile(uploadId: string): void {
    this.cancelUpload.emit({ uploadId });
    this.localStatus.update((file) => ({ ...file, cancelled: true }));

    if (uploadId && !uploadId.startsWith('upload-')) {
      this.cancelUpload.emit({ uploadId });

      return;
    }

    const fileName = this.current()?.file?.name;
    if (!fileName) return;

    this.#store.dispatch(UploadActions.clearFileSuccess({ name: fileName }));
  }

  #syncProgressSubject([
    progressData,
    uploadState,
  ]: [
    StorageUploadProgressPayload,
    CurrentUploadState,
  ]): void {
    const { complete } = uploadState;
    const { progress, uploaded, size } = progressData;

    switch (progress) {
      case -1:
        if (!complete) this.localStatus.update((file) => ({ ...file, cancelled: true }));
        break;
      case 100:
        this.localStatus.set({
          progress: 100,
          size,
          uploaded: size,
          cancelled: false,
        });

        break;
      default:
        this.localStatus.update((file) => ({ ...file, progress, uploaded, size }));
        break;
    }
  }
}
